import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { addDays, addMonths, type DateStr, dateParts, daysInMonth, makeDate, mondayOf, todayInZone, WEEKDAY_NAMES, weekday } from "@justone/shared";
import { formatDueDate } from "../lib/tasks";
import { Popover } from "./Popover";

/** 자주 고르는 날 */
function quickDates(today: DateStr): [string, DateStr][] {
  const wd = weekday(today);
  const saturday = wd >= 5 ? today : addDays(today, 5 - wd);
  return [
    ["오늘", today],
    ["내일", addDays(today, 1)],
    ["이번 주말", saturday],
    ["다음 주 월요일", addDays(mondayOf(today), 7)],
  ];
}

/** 달력 (월요일부터). 화살표로 하루·한 주, PageUp/PageDown으로 한 달 */
export function MiniCalendar({ value, onPick }: { value: DateStr | null; onPick: (d: DateStr) => void }) {
  const today = todayInZone();
  const [focus, setFocus] = useState<DateStr>(value ?? today);
  const grid = useRef<HTMLDivElement>(null);
  const { year, month } = dateParts(focus);
  const first = makeDate(year, month, 1);
  const lead = weekday(first);
  const days = daysInMonth(year, month);
  const cells: (DateStr | null)[] = [...Array<null>(lead).fill(null), ...Array.from({ length: days }, (_, i) => makeDate(year, month, i + 1))];
  while (cells.length % 7) cells.push(null);

  useEffect(() => {
    grid.current?.querySelector<HTMLButtonElement>(`[data-date="${focus}"]`)?.focus({ preventScroll: true });
  }, [focus]);

  const onKeyDown = (e: KeyboardEvent) => {
    const moves: Record<string, () => DateStr> = {
      ArrowLeft: () => addDays(focus, -1),
      ArrowRight: () => addDays(focus, 1),
      ArrowUp: () => addDays(focus, -7),
      ArrowDown: () => addDays(focus, 7),
      PageUp: () => addMonths(focus, -1),
      PageDown: () => addMonths(focus, 1),
      Home: () => mondayOf(focus),
      End: () => addDays(mondayOf(focus), 6),
    };
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    setFocus(move());
  };

  return (
    <div className="mini-cal">
      <div className="mc-head">
        <button type="button" className="icon-btn" onClick={() => setFocus(addMonths(focus, -1))} aria-label="이전 달">
          ‹
        </button>
        <span aria-live="polite">
          {year}년 {month}월
        </span>
        <button type="button" className="icon-btn" onClick={() => setFocus(addMonths(focus, 1))} aria-label="다음 달">
          ›
        </button>
      </div>
      <div ref={grid} className="mc-grid" role="grid" aria-label={`${year}년 ${month}월`} onKeyDown={onKeyDown}>
        <div role="row" className="mc-row">
          {WEEKDAY_NAMES.map((w) => (
            <span key={w} role="columnheader" className={`mc-wd${w === "토" || w === "일" ? " weekend" : ""}`}>
              {w}
            </span>
          ))}
        </div>
        {Array.from({ length: cells.length / 7 }, (_, r) => (
          <div key={r} role="row" className="mc-row">
            {cells.slice(r * 7, r * 7 + 7).map((d, i) =>
              d ? (
                <span key={d} role="gridcell" aria-selected={d === value}>
                  <button
                    type="button"
                    data-date={d}
                    tabIndex={d === focus ? 0 : -1}
                    className={["mc-day", d === value && "on", d === today && "today", d < today && "past", i >= 5 && "weekend"].filter(Boolean).join(" ")}
                    aria-label={`${month}월 ${dateParts(d).day}일 ${WEEKDAY_NAMES[weekday(d)]}요일${d === today ? " (오늘)" : ""}`}
                    onClick={() => onPick(d)}
                  >
                    {dateParts(d).day}
                  </button>
                </span>
              ) : (
                <span key={`e${i}`} role="gridcell" />
              ),
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** "내일 · 10/19", "10월 30일" */
function dateLabel(d: DateStr): string {
  const rel = formatDueDate(d);
  const { month, day } = dateParts(d);
  return /월 \d+일$/.test(rel) ? rel : `${rel} · ${month}/${day}`;
}

/** 날짜 단추 → 빠른 선택 + 달력 */
export function DatePicker({ value, onChange, disabled, label }: { value: DateStr | null; onChange: (d: DateStr | null) => void; disabled?: boolean; label: string }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const today = todayInZone();
  const pick = (d: DateStr | null) => {
    setAnchor(null);
    onChange(d);
  };
  return (
    <>
      <button type="button" className="btn btn-sm date-trigger" disabled={disabled} aria-haspopup="dialog" aria-expanded={!!anchor} aria-label={`${label}: ${value ? formatDueDate(value) : "없음"}`} onClick={(e) => setAnchor(anchor ? null : e.currentTarget)}>
        {value ? dateLabel(value) : "날짜 고르기"}
      </button>
      <Popover anchor={anchor} open={!!anchor} onClose={() => setAnchor(null)} className="date-pop" role="dialog" label={label}>
        <div className="date-quick">
          {quickDates(today).map(([name, d]) => (
            <button key={name} type="button" className={`btn btn-sm${d === value ? " is-on" : ""}`} onClick={() => pick(d)}>
              {name}
            </button>
          ))}
          {value && (
            <button type="button" className="btn btn-sm btn-quiet" onClick={() => pick(null)}>
              없음
            </button>
          )}
        </div>
        <MiniCalendar value={value} onPick={pick} />
      </Popover>
    </>
  );
}
